// import

import {ArticleJsonLd, NextSeo, BlogJsonLd, BreadcrumbJsonLd} from 'next-seo';
import {memo} from 'react';

import {siteUrl} from 'libs/constants';
import type {NodePage} from 'libs/db';
import type {BlogMainNode, NodeType, PostNode, UrlNode} from 'libs/model';
import {isStr} from 'libs/rambo';
import {useSettings} from 'modules/App';

import {getCreateDate, getRollPrefix, getUpdateDate, withPaging} from '../Seo/utils';

// types

type Props = {
  main: BlogMainNode;
  area?: UrlNode;
  page?: NodePage<PostNode>;
  posts?: PostNode[];
};

// fns

const pageOrFirst = (page?: NodePage<PostNode>) =>
  (page?.page && page.page > 1 ? page.page : null);

// component

// eslint-disable-next-line prefer-arrow-callback
export const BlogMainSeo = memo(function BlogMainSeo(props: Props) {
  const {main, area, page, posts} = props;
  const {seo, biz, openGraphLogo} = useSettings();

  const url = [
    siteUrl,
    area?.meta.link ?? main.meta.link,
    pageOrFirst(page) ? `/${page?.page}` : null,
  ].filter(isStr).join('');

  const title = [
    area ? [
      getRollPrefix(area.meta.type as NodeType),
      area.meta.seoTitle || area.meta.title,
    ].join(': ') : undefined,

    (main.meta.seoTitle?.trim() || main.meta.title) +
    (pageOrFirst(page) ? `, page ${page?.page}` : ''),
  ].filter(isStr).join(` ${seo.sep} `);

  const description =
    area?.meta.seoDescription ??
    main.meta.seoDescription;

  const images = [
    area?.meta.photoImage ??
    area?.meta.bannerImage ??
    main.meta.bannerImage ??
    openGraphLogo.url,
  ].filter(isStr);

  return (
    <>
      <NextSeo
        canonical={url}
        description={description.replace(/"/g, '`')}
        openGraph={{
          url,
          title,
          description,
          type: 'website',
          site_name: seo.title,
          images: images.map((img) => ({url: img})),
        }}
        title={title}
      />

      <BlogJsonLd
        authorName={biz.feedAuthor.name}
        dateModified={posts?.length ?
          getUpdateDate(posts[0]) :
          new Date().toISOString()}
        datePublished={posts?.length ?
          getCreateDate(posts[posts.length - 1]) :
          new Date(main.meta.createDate).toISOString()}
        description={description.replace(/"/g, '`')}
        images={images}
        title={title}
        url={url.replace(/\/\d+$/, '')}
      />

      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: main.meta.title,
            item: siteUrl + main.meta.link,
          },
          ...withPaging(page) ? [{
            position: 2,
            name: `Page ${page.page} of ${page.last}`,
            item: url,
          }] :
          area ? [{
            position: 2,
            name: area.meta.title,
            item: url,
          }] : [],
        ]}
      />

      {posts?.map((post) => (
        <ArticleJsonLd
          key={post.meta.id}
          authorName={[post.meta.authorRef?.meta.title ?? biz.feedAuthor.name]}
          dateModified={getUpdateDate(post)}
          datePublished={getCreateDate(post)}
          description={post.meta.seoDescription.replace(/"/g, '`')}
          images={[
            post.meta.bannerImage,
            biz.logoImage,
          ].filter(isStr)}
          keyOverride={post.meta.id}
          publisherLogo={biz.logoImage}
          publisherName={biz.feedAuthor.name}
          title={post.meta.seoTitle?.trim() || post.meta.title}
          url={siteUrl + post.meta.link}
        />
      ))}
    </>
  );
});
